import { Apollo } from 'apollo-angular';

import { Injectable } from '@angular/core';
import {
  DimensionDataGQL,
  DimensionDataQuery,
  OneParkDimensionDataGQL,
  OneParkDimensionDataQuery,
  OneTurbineDimensionDataGQL,
  OneTurbineDimensionDataQuery,
  DisplayNameByParkGQL,
  DisplayNameByParkQuery,
  DimensionDataCountriesGQL,
  DimensionDataCountriesQuery,
  AllCountriesGQL,
  AllCountriesQuery,
  GetAllParksGQL,
  GetAllParksQuery,
  DimensionDataCountriesForPortfolioQuery,
  DimensionDataCountriesForPortfolioGQL,
  OneParkDimensionDataByIdGQL,
} from '../graphql';

import { map } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DimensionDataService {
  constructor(
    private apollo: Apollo,
    private dimensionDataGQL: DimensionDataGQL,
    private allCountriesGQL: AllCountriesGQL,
    private dimensionDataCountriesGQL: DimensionDataCountriesGQL,
    private dimensionDataCountriesForPortfolioGQL: DimensionDataCountriesForPortfolioGQL,
    private oneParkDimensionData: OneParkDimensionDataGQL,
    private oneParkDimensionDataById: OneParkDimensionDataByIdGQL,
    private oneTurbineDimensionData: OneTurbineDimensionDataGQL,
    private displayNameByParkGQL: DisplayNameByParkGQL,
    private getAllParksGQL: GetAllParksGQL,
  ) {}

  public getParksMetadata() {
    return this.apollo
      .use('v2_http')
      .query({
        query: this.dimensionDataGQL.document,
      })
      .pipe(
        map((e: Apollo.QueryResult<DimensionDataQuery>) => {
          return e.data.PARKS;
        }),
      );
  }

  public getCountries() {
    return this.apollo
      .use('v2_http')
      .query({
        query: this.allCountriesGQL.document,
      })
      .pipe(
        map((e: Apollo.QueryResult<AllCountriesQuery>) => {
          return e.data.COUNTRIES;
        }),
      );
  }

  public getCountriesParkMetadata(portfolioId?: string, parkIds?: string[]) {
    if (portfolioId) {
      return this.apollo
        .use('v2_http')
        .query({
          query: this.dimensionDataCountriesForPortfolioGQL.document,
          variables: { portfolioId, parkIds },
          fetchPolicy: 'network-only',
        })
        .pipe(
          map((e: Apollo.QueryResult<DimensionDataCountriesForPortfolioQuery>) => {
            return e.data.PORTFOLIO.COUNTRIES;
          }),
        );
    } else {
      return this.apollo
        .use('v2_http')
        .query({
          query: this.dimensionDataCountriesGQL.document,
        })
        .pipe(
          map((e: Apollo.QueryResult<DimensionDataCountriesQuery>) => {
            return e.data.COUNTRIES;
          }),
        );
    }
  }

  public getOneParkMetadata(parkName: string, turbineIds: string[] = null) {
    return this.apollo
      .use('v2_http')
      .query({
        query: this.oneParkDimensionData.document,
        variables: { parkName, turbineIds: turbineIds },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((e: Apollo.QueryResult<OneParkDimensionDataQuery>) => {
          return e.data.PARKS;
        }),
      );
  }

  public getOneParkMetadataById(parkId: string, turbineIds: string[] = null) {
    return this.apollo
      .use('v2_http')
      .query({
        query: this.oneParkDimensionDataById.document,
        variables: { parkId, turbineIds: turbineIds },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((e: Apollo.QueryResult<OneParkDimensionDataQuery>) => {
          return e.data.PARKS;
        }),
      );
  }

  public getOneTurbineMetadata(turbineSerial: number) {
    return this.apollo
      .use('v2_http')
      .query({
        query: this.oneTurbineDimensionData.document,
        variables: { turbineSerial },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((e: Apollo.QueryResult<OneTurbineDimensionDataQuery>) => {
          return e.data.TURBINES;
        }),
      );
  }

  public getParkDisplayName(park: string) {
    return this.apollo
      .use('v2_http')
      .query({
        query: this.displayNameByParkGQL.document,
        variables: { park },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((e: Apollo.QueryResult<DisplayNameByParkQuery>) => {
          return e.data.PARKS[0]?.displayName;
        }),
      );
  }

  public getAllParks() {
    return this.apollo
      .use('v2_http')
      .query({
        query: this.getAllParksGQL.document,
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((e: Apollo.QueryResult<GetAllParksQuery>) => {
          return e.data.PARKS;
        }),
      );
  }
}
